import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as dayjs from 'dayjs';

import { useNavigate } from 'react-router-dom';
import { Container } from '../../../ui/gridSystem';
import {
  Badge,
  CTA,
  Checkbox,
  CustomSelect,
  NoResult,
  Text,
} from '../../../ui/atoms';
import {
  ActionsWrapper,
  CheckboxWrapper,
  Filter,
  FiltersWrapper,
  Legend,
  NotificationBody,
  NotificationCard,
  NotificationDate,
  NotificationStatus,
  NotificationWrapper,
} from './style';
import { withMediaQueries } from '../../hoc/withMediaQueries';
import {
  NOTIFICATION_DELETE,
  NOTIFICATION_GET,
  NOTIFICATION_READ,
} from '../../redux/actions/notifications';
import {
  CONSUMER_NOTIFICATIONS,
  SUPERVISOR_NOTIFICATIONS,
  TUTOR_NOTIFICATIONS,
  getLabelByType,
  getUrlFromNotification,
  replaceCourseSubstrings,
} from '../../../utils/notifications';
import { Status } from '../../../ui/components/NavBar/Desktop/style';
import Pagination from '../../../ui/atoms/Pagination';
import SortBy from '../../../ui/atoms/SortBy';
import { StickyFiltersWrapper } from '../../../ui/atoms/CTA/style';
import ModalFilters, {
  ModalFiltersId,
} from '../../../ui/atoms/Modals/ModalFilters';
import { MODAL_OPEN } from '../../redux/actions';
import ModalConfirm, {
  ModalConfirmId,
} from '../../../ui/atoms/Modals/ModalConfirm';
import { getSortingOptions } from '../../../utils/labels';

const sortingIds = ['created_desc', 'not_read', 'alphabetical'];

const Notifications = ({
  notifications,
  pager,
  isTutor,
  isSupervisor,
  getNotification,
  readNotification,
  deleteNotification,
  mediaIsPhone,
  modalOpen,
}) => {
  const navigate = useNavigate();
  const [filters, setfilters] = useState(CONSUMER_NOTIFICATIONS);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [multipleSelection, setMultipleSelection] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSupervisor) {
      setfilters(SUPERVISOR_NOTIFICATIONS);
    } else if (isTutor) {
      setfilters(TUTOR_NOTIFICATIONS);
    }
  }, [isTutor, isSupervisor]);

  const handleGetNotifications = (page) => {
    const parsedFilters = {
      ...selectedFilters,
      page,
    };
    getNotification(parsedFilters);
  };

  useEffect(() => {
    handleGetNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  const handleSelectNotification = (notification) => {
    const newSelection = [...multipleSelection];
    const oldNotificationIndex = multipleSelection.findIndex(
      (nid) => nid === notification?.id
    );
    if (oldNotificationIndex < 0) {
      newSelection.push(notification.id);
    } else {
      newSelection.splice(oldNotificationIndex, 1);
    }
    setMultipleSelection(newSelection);
  };

  const clickNotification = (item) => {
    if (item.status === 0) readNotification({ ids: [item.id], status: 1 });
    const url = getUrlFromNotification(item);
    navigate(url);
  };
  const handleNotificationClick = (item) => {
    if (mediaIsPhone) handleSelectNotification(item);
  };

  const handleClickDelete = () => {
    modalOpen(ModalConfirmId);
  };
  const confirmDelete = () => {
    const params = {
      action: selectedFilters?.action,
      page: pager?.page,
      items_per_page: pager?.items_per_page,
    };
    deleteNotification({ ids: multipleSelection, params });
    setMultipleSelection([]);
  };
  const handleClickMarkAsRead = (status) => {
    const params = {
      ...selectedFilters,
      page: pager?.page,
      items_per_page: pager?.items_per_page,
    };
    readNotification({
      ids: multipleSelection,
      status,
      params,
    });
    setMultipleSelection([]);
  };

  const selectAll = () => {
    if (multipleSelection?.length === notifications?.length) {
      setMultipleSelection([]);
    } else {
      const allIds = notifications?.map((n) => n?.id);
      setMultipleSelection(allIds);
    }
  };

  const handleOnChange = ({ key, value }) => {
    const newFilters = { ...selectedFilters };
    newFilters[key] = value;
    setSelectedFilters(newFilters);
  };

  const loadMore = (page) => handleGetNotifications(page);
  const setItemsPerPage = (v) =>
    handleOnChange({ key: 'items_per_page', value: v?.value });

  return (
    <>
      <Container>
        <NotificationWrapper>
          <Text type="h1" mb={mediaIsPhone ? 12 : 40}>
            Notifiche
          </Text>
          {mediaIsPhone ? (
            <StickyFiltersWrapper mb={0}>
              <CTA
                btnText="Filtri"
                type="secondary"
                fullsize
                onClick={() =>
                  modalOpen(ModalFiltersId, {
                    filters: {
                      action: filters,
                    },
                    activeFilters: selectedFilters,
                    sorting: getSortingOptions(sortingIds),
                  })
                }
              />
            </StickyFiltersWrapper>
          ) : (
            <FiltersWrapper>
              <Filter>
                <CustomSelect
                  placeholder="Tipologia"
                  isClearable
                  options={filters}
                  selected={filters?.find(
                    (f) => f?.value === selectedFilters?.action
                  )}
                  onChange={(value) =>
                    handleOnChange({ key: 'action', value: value?.value })
                  }
                />
              </Filter>
              <SortBy
                options={getSortingOptions(sortingIds)}
                onChange={({ value }) =>
                  handleOnChange({ key: 'sort_by', value })
                }
              />
            </FiltersWrapper>
          )}
          {notifications?.length > 0 ? (
            <>
              <ActionsWrapper>
                <Legend>
                  <Checkbox
                    onChange={selectAll}
                    checked={
                      multipleSelection?.length === notifications?.length
                    }
                    label="Seleziona tutto"
                  />
                </Legend>
                <ActionsWrapper className="inline">
                  <CTA
                    type="tertiary"
                    btnText="Elimina"
                    icon="trash-03"
                    onClick={handleClickDelete}
                    disabled={multipleSelection?.length <= 0}
                  />
                  <CTA
                    type="tertiary"
                    btnText="Segna come lette"
                    icon="eye"
                    onClick={() => handleClickMarkAsRead(1)}
                    disabled={multipleSelection?.length <= 0}
                  />
                  <CTA
                    type="tertiary"
                    btnText="Segna come non lette"
                    icon="eye-off"
                    onClick={() => handleClickMarkAsRead(0)}
                    disabled={multipleSelection?.length <= 0}
                  />
                </ActionsWrapper>
              </ActionsWrapper>
              {(notifications || []).map((item) => (
                <NotificationCard
                  key={`notification-${item?.id}`}
                  isActive={multipleSelection?.includes(item?.id)}
                  className={item?.status === 0 ? 'not-read' : ''}
                  onClick={() => handleNotificationClick(item)}
                >
                  <CheckboxWrapper>
                    <Checkbox
                      onChange={() => handleSelectNotification(item)}
                      checked={multipleSelection?.includes(item?.id)}
                      label=" "
                    />
                  </CheckboxWrapper>
                  <Badge type="news">{getLabelByType(item)}</Badge>
                  <NotificationDate>
                    {dayjs(new Date((item?.created || 0) * 1000)).format(
                      'DD/MM/YYYY HH:mm'
                    )}
                  </NotificationDate>
                  <NotificationStatus>
                    {item?.status === 0 && <Status />}
                  </NotificationStatus>
                  <NotificationBody>
                    <Text type="h2" mb={8}>
                      {item?.title}
                    </Text>
                    <Text type="b3" mb={8}>
                      {replaceCourseSubstrings({ input: item?.body })}
                    </Text>
                  </NotificationBody>
                  <CTA
                    type="tertiary"
                    btnText="Dettaglio"
                    onClick={() => clickNotification(item)}
                    icon="arrow-narrow-right"
                    bottomRight
                  />
                </NotificationCard>
              ))}
            </>
          ) : (
            <NoResult />
          )}
        </NotificationWrapper>
        <Pagination
          pager={pager}
          callBack={loadMore}
          setItemsPerPage={setItemsPerPage}
        />
      </Container>
      <ModalConfirm title="Elimina notifiche" onClick={() => confirmDelete()}>
        <Text type="b3">
          Vuoi eliminare definitivamente le notifiche selezionate?
        </Text>
      </ModalConfirm>
      {mediaIsPhone && (
        <ModalFilters
          onClick={(newFilters) => setSelectedFilters(newFilters)}
        />
      )}
    </>
  );
};

Notifications.propTypes = {
  // HOC withMediaQueries
  mediaIsPhone: PropTypes.bool,
  //HOC (connect, state)
  notifications: PropTypes.array,
  pager: PropTypes.object,
  isTutor: PropTypes.bool,
  isSupervisor: PropTypes.bool,

  // HOC (connect, dispatch)
  getNotification: PropTypes.func.isRequired,
  readNotification: PropTypes.func.isRequired,
  deleteNotification: PropTypes.func.isRequired,
  modalOpen: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    const { data: notifications, pager } = state.notifications;
    const { isTutor, isSupervisor } = state.user;
    return {
      notifications,
      pager,
      isTutor,
      isSupervisor,
    };
  },
  (dispatch) => ({
    getNotification: (params) =>
      dispatch({ type: NOTIFICATION_GET._REQUEST, filters: params }),
    readNotification: ({ ids, status, params }) =>
      dispatch({
        type: NOTIFICATION_READ._REQUEST,
        ids,
        status,
        filters: params,
      }),
    deleteNotification: ({ ids, params }) =>
      dispatch({ type: NOTIFICATION_DELETE._REQUEST, ids, filters: params }),
    modalOpen: (id, payload) => dispatch({ type: MODAL_OPEN, id, payload }),
  })
)(withMediaQueries(Notifications));
